import { Link, useNavigate } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import { useLogout, useMe } from "@/lib/api";
import { PROJECT_NAME } from "@/lib/brand-assets";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.

interface AppHeaderProps {
  /** Which side of the app this is, e.g. "BOI cockpit" or "Client portal". */
  side: string;
}

/**
 * Top bar shared by the BOI cockpit and the client portal: brand, side label,
 * Profile, the admin links (admins only) and Sign out.
 */
export function AppHeader({ side }: AppHeaderProps) {
  const navigate = useNavigate();
  const logoutMutation = useLogout();
  const { data: me } = useMe();

  const handleLogout = () => {
    logger.debug("[auth] signing out");
    // Fire-and-forget on purpose: chained on the promise, not a per-call
    // `onSuccess`, because TanStack drops those once this header unmounts (the
    // user can click away mid-request), and the redirect must happen anyway.
    void logoutMutation.mutateAsync().then(
      () => navigate("/login"),
      (err: unknown) => logger.warn("[auth] sign-out failed:", err),
    );
  };

  return (
    <header className="border-border bg-background sticky top-0 z-40 flex flex-wrap items-center justify-between gap-y-3 border-b px-6 py-4 md:px-12">
      <div className="flex shrink-0 items-center gap-5">
        <BrandLogo name={PROJECT_NAME} className="h-6 text-xl" />
        <span className="border-border text-muted t-eyebrow hidden border-l pl-5 sm:inline">
          {side}
        </span>
      </div>
      <nav className="flex flex-wrap items-center gap-1 md:gap-2">
        {me?.role === "admin" && (
          <>
            <Link to="/admin/users">
              <Button variant="ghost" size="sm">
                Admin
              </Button>
            </Link>
            <Link to="/admin/activity">
              <Button variant="ghost" size="sm">
                Activity
              </Button>
            </Link>
            <Link to="/admin/workspaces">
              <Button variant="ghost" size="sm">
                Workspaces
              </Button>
            </Link>
            <Link to="/admin/nda">
              <Button variant="ghost" size="sm">
                NDA
              </Button>
            </Link>
          </>
        )}
        <Link to="/profile">
          <Button variant="ghost" size="sm">
            Profile
          </Button>
        </Link>
        <Button
          variant="outline"
          size="sm"
          className="ml-2"
          onClick={handleLogout}
          disabled={logoutMutation.isPending}
        >
          Sign out
        </Button>
      </nav>
    </header>
  );
}
